<template lang="pug">
SingaDropdown.is-bottom-left(v-if="activeSortFilter" v-model="activeSortFilter" aria-role="menu" :mobileModal="false")
	template(#trigger="{ active }")
		SingaButton.is-small.is-transparent-dark.filter-button(
			slot="trigger"
			:icon-right="active ? 'caret-up-outline' : 'caret-down-outline'"
			:label="props.showLabel ? activeSortFilter.name : null")
			SingaIcon.filter-icon(v-if="!props.showLabel" icon="filter")
	ul
		SingaDropdownItem.font-size.dropdown-wrapper(aria-role="menuitem" v-for="(item) in sortMenu" :key="item.id" :value="item" :class="{ 'active': item.id === activeSortFilter.id }" tag="li") {{item.name}}
			SingaIcon.icon(:icon="item.id === activeSortFilter.id ? 'checkmark' : null" size="medium" v-if="item.id === activeSortFilter.id")
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'

const route = useRoute()
const searchStore = useSearchStore()
const { addToSelectedFilter } = searchStore
const { getSort } = storeToRefs(searchStore)

const emit = defineEmits(['sortFilterChanged'])

const props = defineProps({
	showLabel: {
		type: Boolean,
		default: true
	},
	filter: {
		type: String,
		default: () => null
	},
	filtersToHide: {
		type: Array,
		default: () => []
	},
	sortValue: {
		type: String,
		default: () => null
	},
	isFromGenrePage: {
		type: Boolean,
		default: false
	}
})

const allSortFilters = useSortMenu()
const sortMenu = computed(() => {
	return allSortFilters.filter(item => !props.filtersToHide.includes(item.id))
})

const activeSortFilter = ref()

const index = sortMenu.value.findIndex(option => option.id === props.filter)

if (props.filter && index) {
	activeSortFilter.value = sortMenu.value[index]
}

const findSortIndex = (routeQuery: any) => {
	const sortIndex = sortMenu.value.findIndex(item => item.id === routeQuery)
	return sortIndex ? sortMenu.value[sortIndex] : sortMenu.value[0]
}

// Add a watcher to keep in sync with the genre page modal
if (props.isFromGenrePage) {
	watch(() => route.query, (value, newValue) => {
		if (value.sort !== newValue.sort) {
			activeSortFilter.value = route.query.sort
				? findSortIndex(route.query.sort)
				: props.sortValue
					? findSortIndex(props.sortValue)
					: findSortIndex(getSort.value) || 'top'
		}
	})
}

watch(route, (value) => {
	if (value.query.sort) {
		activeSortFilter.value = value.query.sort
			? findSortIndex(value.query.sort)
			: props.sortValue
				? findSortIndex(props.sortValue)
				: findSortIndex(getSort.value) || sortMenu.value[0]
	}
}, { immediate: true, deep: true })

watch(activeSortFilter, async (value) => {
	await addToSelectedFilter('sort', value.id)
	emit('sortFilterChanged', value)
})

onMounted(() => {
	if (!activeSortFilter.value) {
		activeSortFilter.value = props.sortValue
			? findSortIndex(props.sortValue)
			: findSortIndex(getSort.value) || sortMenu.value[0]
	}
})
</script>

<style lang="sass" scoped>
.filter-icon
	margin-right: $spacing-8 !important
.filter-button
	max-height: 100%
	@include fontSize(s)
	:deep(.button-wrapper)
		max-width: none
		justify-content: space-between

:deep(.button-wrapper)
	span
		max-width: none
		text-overflow: ellipsis
		white-space: nowrap
		overflow: hidden
		@media(max-width: 400px)
			max-width: 125px

.active
	@include font(basier, medium)

.font-size
	@include fontSize(s)

.icon-inner
	margin-left: $spacing-16

.dropdown-wrapper
	display: flex
	justify-content: space-between
	align-items: center

:deep(.button.is-small span.icon)
	margin-left: $spacing-8
	max-width: 16px
	max-height: 16px

:deep(.dropdown-menu)
	overflow: visible
	margin-top: $spacing-16

:deep(.dropdown-menu:before)
	bottom: 100%
	left: 82.5%
	border: solid transparent
	content: ""
	height: 0
	width: 0
	position: absolute
	pointer-events: none
	border-bottom-color: $color-grey-80
	border-width: 10px
</style>
